/**
 * /placements/scripts/:scriptId — a placement inventory read out of the database.
 *
 * The third way into the same table. /placements shows a checked-in snapshot,
 * /placements/live runs the agent on an upload, and this one shows what the agent stored when a
 * script was uploaded for real. All three render through PlacementInventoryPage, so the table
 * cannot look different depending on where the data came from.
 *
 * Two things this page is responsible for that the table is not:
 *
 * 1. SAYING WHAT PRODUCED THESE NUMBERS. The API returns the most recently *computed* run, which
 *    is not the same as "the current prompt" — those diverge the moment someone re-runs an older
 *    configuration to compare. So the prompt version and model are on screen rather than
 *    implied, and older runs are reachable instead of hidden.
 * 2. DISTINGUISHING "never analysed" FROM "analysis failed". A 404 means nothing has run; a
 *    stored failure comes back as a normal response carrying its reason. Collapsing those into
 *    one empty state is how a script silently never gets looked at again.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, FileQuestion, Loader2, LogIn, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PlacementInventoryPage from "@/pages/PlacementInventoryPage";
import {
  PlacementFetchError,
  fetchLatestInventory,
  fetchRunByHash,
  fetchRunHistory,
} from "@/api/placements";
import type { RunListEntry, StoredPlacementRun } from "@/types/placement-run";

const when = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString() : "unknown time";

/** Shell so every state on this page sits in the same frame as the table. */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto space-y-4">
        <Link
          to="/placements"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Saved Detours run
        </Link>
        {children}
      </div>
    </div>
  );
}

export default function StoredPlacementPage() {
  const { scriptId = "" } = useParams();
  const [run, setRun] = useState<StoredPlacementRun | null>(null);
  const [history, setHistory] = useState<RunListEntry[]>([]);
  const [error, setError] = useState<PlacementFetchError | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const latest = await fetchLatestInventory(scriptId);
      setRun(latest);
      // History is a nicety, not a requirement: if it fails the inventory still renders.
      try {
        const { runs } = await fetchRunHistory(scriptId);
        setHistory(runs);
      } catch {
        setHistory([]);
      }
    } catch (cause) {
      setError(
        cause instanceof PlacementFetchError
          ? cause
          : new PlacementFetchError(String(cause), 0),
      );
      setRun(null);
    } finally {
      setLoading(false);
    }
  }, [scriptId]);

  useEffect(() => {
    void load();
  }, [load]);

  const showRun = async (runHash: string) => {
    setLoading(true);
    try {
      setRun(await fetchRunByHash(runHash));
    } catch (cause) {
      setError(
        cause instanceof PlacementFetchError ? cause : new PlacementFetchError(String(cause), 0),
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading && !run) {
    return (
      <Frame>
        <Card>
          <CardContent className="py-10 flex items-center justify-center gap-2 text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading the stored inventory…
          </CardContent>
        </Card>
      </Frame>
    );
  }

  if (error) {
    // Each status means something different to whoever is looking at it, so each gets its own
    // wording and its own next action. A single "something went wrong" would be useless here.
    const notAnalysed = error.status === 404;
    const needsAuth = error.status === 401;
    const unreachable = error.status === 0;
    return (
      <Frame>
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              {needsAuth ? (
                <LogIn className="h-4 w-4" />
              ) : notAnalysed ? (
                <FileQuestion className="h-4 w-4" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-600" />
              )}
              {needsAuth
                ? "Sign in to see this inventory"
                : notAnalysed
                  ? "This script has not been analysed"
                  : unreachable
                    ? "Cannot reach the API"
                    : "Could not load the inventory"}
            </CardTitle>
            <CardDescription>{error.message}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-600">
            {notAnalysed && (
              <p>
                Nothing has run against this script yet. That is different from a run that
                failed — a failed run would show up here with its reason. Uploading the script
                with the agent enabled starts one, or you can run it yourself at{" "}
                <Link to="/placements/live" className="underline">
                  /placements/live
                </Link>
                .
              </p>
            )}
            {unreachable && (
              <p>
                The backend is not answering. Start it with{" "}
                <code>uvicorn app.main:app --reload --port 8000</code>.
              </p>
            )}
            <Button variant="outline" size="sm" onClick={() => void load()}>
              <RefreshCw className="mr-2 h-3 w-3" />
              Try again
            </Button>
          </CardContent>
        </Card>
      </Frame>
    );
  }

  if (!run) return null;

  const failed = run.status !== "complete";

  return (
    <div>
      <div className="bg-slate-50 pt-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto space-y-3">
          <Link
            to="/placements"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Saved Detours run
          </Link>

          {/* What produced these numbers. On screen rather than implied, because the run shown
              is the most recently COMPUTED one, which is not necessarily the current prompt. */}
          <Card className="border-slate-200">
            <CardContent className="py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <span>
                <Badge variant="outline" className="mr-2 font-normal">
                  stored run
                </Badge>
                analysed {when(run.ts_at)}
              </span>
              {run.provenance.creator_name && (
                <span className="text-slate-600">
                  uploaded by <strong className="text-slate-800">{run.provenance.creator_name}</strong>
                </span>
              )}
              <span className="font-mono text-xs text-slate-500">
                {run.provenance.model ?? "unknown model"} · prompt{" "}
                {run.provenance.prompt_bundle ?? "?"}/{run.provenance.prompt_version ?? "?"} ·{" "}
                {run.provenance.votes ?? "?"} readings
              </span>

              {history.length > 1 && (
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-slate-500">version</span>
                  <Select value={run.run_hash ?? ""} onValueChange={(v) => void showRun(v)}>
                    <SelectTrigger className="h-8 w-[280px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {history.map((entry) => (
                        <SelectItem
                          key={entry.run_hash ?? ""}
                          value={entry.run_hash ?? ""}
                          className="text-xs"
                        >
                          {when(entry.ts_at)} · prompt {entry.provenance.prompt_version ?? "?"} ·{" "}
                          {entry.totals.products ?? 0} products
                          {entry.status !== "complete" ? ` · ${entry.status}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {failed && (
            <Card className="border-amber-300 bg-amber-50">
              <CardContent className="py-3 text-sm text-amber-900">
                <strong>This run did not produce an inventory ({run.status}).</strong>{" "}
                {run.error}
                {history.length > 1 && (
                  <> An earlier run may have succeeded — check the version list above.</>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* The same table as everywhere else. A stored run cannot render differently from a live
          one, because there is only one implementation of it. */}
      {!failed && <PlacementInventoryPage data={run} />}
    </div>
  );
}
